// The `Api` on the platform's client core (@hq-org/platform-wasm: the kit's sync engine with the chat and board features,
// Decision sync-engine), the web's one client: the board and chat are the core's feature views (`watch`/`look`, over
// the local replica, kept current by the log) and intents (`act`: optimistic writes); other records are the kit's
// declared queries; everything else is a one-shot `ask` (me, session, usage, kinds, search, record, history, install).
// The rules, the documents, the auth header, retries and the error taxonomy live in the core; this module only turns
// answers into the pages' shapes. Plain HTTP for what is not GraphQL is net.ts.
import type { Kit, KitError, KindInfo, Query, Row, Watching, Written } from "@hq-org/kit-wasm";
import type { Net } from "./net";
import {
	type Api,
	ApiError,
	type Attachment,
	type BoardQuery,
	type BoardView,
	type ChatOverview,
	type ContextItem,
	Detail,
	type EdgeKind,
	type Field,
	type FileVersion,
	instanceOf,
	type Conversation,
	local,
	type MdDoc,
	type Message,
	type Package,
	type ReviewSource,
	Person,
	Session,
	shortKind,
	type Snapshot,
	type Space,
	type Thread,
	Meter,
	operatorSilentSince,
} from "./types";

type Raw = Record<string, any>;
const MERGE = ["*"];
const K = {
	task: "hq.tasks.Task",
	repo: "hq.tasks.Repo",
	space: "truss.Space",
	person: "truss.Person",
	member: "truss.Member",
	role: "truss.Role",
	connection: "truss.Connection",
	ontology: "truss.Ontology",
	secret: "truss.Secret",
	deviceKey: "truss.DeviceKey",
	thread: "hq.chat.Thread",
	message: "hq.chat.Message",
	reaction: "hq.chat.Reaction",
	readMark: "hq.chat.ReadMark",
	instance: "hq.infra.Instance",
	hosting: "hq.infra.Hosting",
	deployment: "hq.infra.Deployment",
	proposal: "hq.knowledge.Proposal",
	batch: "hq.knowledge.Batch",
	source: "hq.knowledge.Source",
	folder: "hq.files.Folder",
	file: "hq.files.File",
	version: "hq.files.Version",
} as const;

const str = (v: unknown) => (typeof v === "string" ? v : null);
/** A relation's value as ids (one or many, ids or rows). */
const refs = (v: unknown) => ([] as unknown[]).concat(v ?? []).map((r) => (typeof r === "string" ? r : ((r as Raw)?.id as string)));
/** An hq.knowledge.Source row as the review shows it. */
const toSource = (s: Raw): ReviewSource => ({
	id: s.id as string,
	type: str(s.type) ?? "FILE",
	title: str(s.title),
	path: str(s.path),
	lines: (s.lines as ReviewSource["lines"] | undefined) ?? null,
	page: typeof s.page === "number" ? s.page : null,
	date: str(s.date),
	quote: str(s.quote),
	file: refs(s.file)[0] ?? null,
});

/** A proposal's patch as rows: plain values as they are, the rest as JSON; the housekeeper's own keys left out. */
const patchRows = (patch: unknown): [string, string][] =>
	Object.entries((patch ?? {}) as Raw)
		.filter(([k]) => !k.startsWith("_"))
		.map(([k, v]) => [k, typeof v === "string" ? v : JSON.stringify(v)]);

/** A relation's value as the replica gives it: an id, {id}, or a list of either: the first id. */
const refOf = (v: unknown): string | null => {
	const x = ([] as unknown[]).concat(v ?? [])[0];
	return typeof x === "string" ? x : ((x as Raw | undefined)?.id ?? null);
};
const versionOf = (v: Raw): FileVersion => ({
	id: v.id,
	file: refOf(v.file) ?? "",
	n: Number(v.n),
	sha256: v.sha256,
	blob: v.blob,
	size: Number(v.size),
	contentType: v.contentType,
	at: (v.createdAt as string) ?? null,
});
/** The kinds the viewers read as text. */
const textual = (type: string) => type.startsWith("text/") || /json|yaml|xml|javascript|csv/.test(type);
/** A file's media type: the browser's, else by its extension (it gives none for .md, .yaml). */
const EXT: Record<string, string> = { md: "text/markdown", markdown: "text/markdown", json: "application/json", yaml: "application/yaml", yml: "application/yaml", txt: "text/plain", csv: "text/csv", pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", svg: "image/svg+xml" };
const typeOf = (f: File) => f.type || EXT[f.name.split(".").pop()?.toLowerCase() ?? ""] || "application/octet-stream";

const errText = (e: { type: string; code?: string; message?: string } | null | undefined) => (e ? (e.message ?? e.code ?? e.type) : "error");

/** A feature view once it is ready (what it reads is on this device): its data; rejects on an error view. */
function seen<T>(k: Kit, view: string, params: unknown = null): Promise<T> {
	return new Promise((ok, fail) => {
		let done = false;
		const w = k.watch<T>(view, params, (v) => {
			if (done || v.status === "loading") return;
			done = true;
			queueMicrotask(() => w.close());
			if (v.status === "ready") ok(v.data as T);
			else fail(toApiError({ error: v.error, message: errText(v.error) }));
		});
	});
}

/** `board.snapshot`'s params: the spaces the session reads in full (KitSession; roles inherit down), so the core
 *  tells the stub ancestors above them. Unknown (the session not answering): null, every space a pick. */
export async function snapshotParams(k: Kit): Promise<{ readable: string[] } | null> {
	try {
		const s = await k.ask<{ spaces?: { space: { id: string } }[] }>({ type: "session" });
		return s?.spaces ? { readable: s.spaces.map((x) => x.space.id) } : null;
	} catch {
		return null;
	}
}

/** The core's board (`board.snapshot`, typed by platform:Interface/board): what the core answers, as it is. */
export function snapshotOf(data: unknown): { success: true; data: Snapshot } | { success: false; error: { issues: { path: string[]; message: string }[] } } {
	const d = data as Snapshot | null;
	return d && Array.isArray(d.tasks) ? { success: true, data: d } : { success: false, error: { issues: [{ path: ["tasks"], message: "not a board snapshot" }] } };
}

/** The core's error in the pages' terms (status codes they switch on). */
export function toApiError(e: unknown): unknown {
	const err = (e as KitError | undefined)?.error;
	if (!err || typeof err !== "object" || !("type" in err)) return e;
	const status = err.type === "unauthorized" ? 401 : err.type === "not_found" ? 404 : err.type === "refused" ? (err.code === "forbidden" ? 403 : 400) : 0;
	return new ApiError((e as Error).message, status);
}

/** A record's instance as a `from` list for its queries (the home's own = absent). */
const fromFor = (id: string | undefined) => {
	const i = id ? instanceOf(id) : null;
	return i ? [i] : undefined;
};
const LABELS = ["name", "title", "path", "key", "emoji", "body", "handle", "instance"];
const text = (key: string, v: unknown): Field[] => (v === null || v === undefined || v === "" ? [] : [{ key, value: typeof v === "string" ? v : JSON.stringify(v) }]);

export type KitSource = {
	/** The signed-in session's kit (one per instance and person). */
	kit: () => Promise<Kit>;
	/** A kit for one question before the session has a person (sign-in: who is this token). */
	once: (token: string) => Promise<Kit>;
	net: Net;
	refresh: () => Promise<string | null>;
	/** The signed-in person's id, when known (sign-in asked once). */
	person: () => string | null;
};

export function createKitApi(src: KitSource): Api {
	const { net } = src;
	/** Runs `f` on the session's kit; an expired credential refreshes once and runs it again. */
	const run = async <T>(f: (k: Kit) => Promise<T>): Promise<T> => {
		const k = await src.kit();
		try {
			return await f(k);
		} catch (e) {
			if ((e as KitError).error?.type !== "unauthorized" || !(await src.refresh())) throw toApiError(e);
			return f(k).catch((e2) => {
				throw toApiError(e2);
			});
		}
	};
	const read = (q: Query) => run((k) => k.read(q));
	const markdown = (text: string) => run(async (k) => (await k.look<MdDoc>("prose.markdown", { text }).data) ?? null);
	const ask = <T>(a: Parameters<Kit["ask"]>[0]) => run((k) => k.ask<T>(a));
	const landed = async (w: Written) => {
		const out = await w.done;
		if (out.type === "refused") throw toApiError({ error: out.error, message: "message" in out.error && out.error.message ? out.error.message : out.error.type === "refused" ? out.error.code : out.error.type });
		return out.id;
	};
	const create = (kind: string, fields: Raw) => run((k) => landed(k.create(kind, fields)));
	const update = (kind: string, id: string, patch: Raw) => run((k) => landed(k.update(kind, id, patch)));
	// A decision on a batch tells its housekeeper: a message in the batch's thread that mentions who made the batch
	// (an agent wakes on a mention, hq agents' runner contract), unless that is the one deciding.
	const wakeHousekeeper = async (proposal: string, body: string) => {
		const [p] = await read({ kind: K.proposal, where: { id: proposal }, limit: 1 });
		const batch = refOf(p?.batch);
		if (!batch) return;
		const [b] = await read({ kind: K.batch, where: { id: batch }, limit: 1 });
		const by = refOf(b?.createdBy);
		const thread = refOf(b?.thread);
		if (!by || !thread || by === (await me()).id) return;
		await create(K.message, { space: refOf(b?.space), thread, body, mentions: { add: [by] } });
	};
	/** A feature view once ready. */
	const see = <T>(view: string, params: unknown = null) => run((k) => seen<T>(k, view, params));
	/** A feature view, now and on every change (ready data, or the error); returns the close. */
	const watched = <T>(view: string, params: unknown, cb: (data: T | null, error: string | null) => void) => {
		let closed = false;
		let w: Watching | null = null;
		void src.kit().then(
			(k) => {
				if (closed) return;
				w = k.watch<T>(view, params, (v) => {
					if (v.status === "error") cb(null, errText(v.error));
					else if (v.status === "ready" && v.data) cb(v.data, null);
				});
			},
			(e) => cb(null, e instanceof Error ? e.message : String(e)),
		);
		return () => {
			closed = true;
			w?.close();
		};
	};
	/** A feature intent: its writes show at once; resolves with its reply once they landed, rejects on a refusal. */
	const act = <T>(name: string, params: unknown) =>
		run(async (k) => {
			const a = k.act<T>(name, params);
			const out = await a.done;
			if (out?.type === "refused") throw toApiError({ error: out.error, message: errText(out.error as { type: string }) });
			return a.reply;
		});

	// who I am: the session's person (its name from the replica), asked only when the session does not know it
	const meOf = new WeakMap<Kit, Promise<Raw>>();
	const me = () =>
		run(async (k) => {
			const id = src.person();
			if (id) return { id, name: ((await read({ kind: K.person, where: { id } }).catch(() => []))[0]?.name as string) ?? id };
			let p = meOf.get(k);
			if (!p) {
				p = k.ask<Raw>({ type: "me" });
				p.catch(() => meOf.delete(k));
				meOf.set(k, p);
			}
			return p;
		});
	const kinds = () => ask<KindInfo[]>({ type: "kinds" });
	/** The kind of a record id, by its prefix. */
	const kindOf = async (id: string) => {
		const prefix = local(id).split("_")[0];
		return (await kinds()).find((k) => k.prefix === prefix) ?? null;
	};
	const has = async (kind: string) => (await kinds()).some((k) => k.kind === kind);

	return {
		session: {
			async me(token) {
				// a session that knows its person (restored, refreshed): its record in the replica, no request
				const id = src.person();
				if (id) {
					const p = (await read({ kind: K.person, where: { id } }).catch(() => []))[0];
					if (p) return Person.parse({ id: p.id, name: p.name ?? p.id, kind: p.type });
				}
				const k = await src.once(token);
				try {
					const m = await k.ask<Raw>({ type: "me" });
					return Person.parse({ id: m.id, name: m.name, kind: m.type });
				} catch (e) {
					throw toApiError(e);
				} finally {
					k.close();
				}
			},
			async get() {
				const [s, instance] = await Promise.all([ask<Raw>({ type: "session" }), net.instance()]);
				return Session.parse({
					person: s.person,
					instance,
					tokenName: s.credential + (s.via ? ` via ${s.via}` : ""),
					permissions: s.permissions,
					projects: (s.spaces ?? []).map((x: Raw) => ({ project: { id: x.space.id }, permissions: x.permissions })),
				});
			},
		},
		snapshot: {
			async load() {
				const parsed = snapshotOf(await run(async (k) => seen(k, "board.snapshot", await snapshotParams(k))));
				if (!parsed.success) throw new ApiError(`board: ${parsed.error.issues[0]?.path.join(".")} ${parsed.error.issues[0]?.message}`);
				return parsed.data;
			},
		},
		board: {
			view(q, cb) {
				let closed = false;
				let params: BoardQuery = q;
				let w: Watching | null = null;
				void src.kit().then(
					(k) => {
						if (closed) return;
						w = k.watch<BoardView>("board.view", params, (v) => v.data && cb(v.data));
					},
					() => {},
				);
				return {
					update(next) {
						params = next;
						w?.update(next);
					},
					close() {
						closed = true;
						w?.close();
					},
				};
			},
			move: async (id, status) => void (await act("board.move", { id, status })),
			place: async (id, status, after) => void (await act("board.place", { id, status, after })),
			columns: (space, cb) => watched("board.columns", { space }, cb),
			column: async (edit) => void (await act("board.column", edit)),
		},
		tasks: {
			async context(id, change) {
				await update(K.task, id, { context: { add: change.add ?? [], remove: change.remove ?? [], note: change.note ?? null } });
			},
			search: (q) => ask<string[]>({ type: "search", kind: K.task, text: q, from: MERGE }),
		},
		entities: {
			async get(id) {
				// a view over the replica (no request); a record this device does not hold is asked for
				const [d, info] = await Promise.all([ask<Raw>({ type: "detail", id }).catch(() => null), kindOf(id)]);
				const out = Detail.parse(d ? fromReplica(d, info) : toDetail(await ask<Raw>({ type: "record", id }), info));
				// a markdown body as the core's document tree (rigid-core parses it)
				if (out.markdown && out.body) out.doc = (await markdown(out.body)) ?? undefined;
				return out;
			},
		},
		instances: {
			async list() {
				const home = { name: (await net.instance()) || "home", url: net.base(), home: true, live: true };
				if (!(await has(K.connection))) return [home];
				const cs = await read({ kind: K.connection, where: { status: "active" } });
				return [home, ...cs.map((c) => ({ name: String(c.instance), url: String(c.url), home: false, live: false }))];
			},
		},
		graph: {
			async load() {
				const skip = new Set<string>([K.ontology, "truss.Hook", K.role, K.readMark, K.reaction, K.message]);
				const ks = (await kinds()).filter((k) => !skip.has(k.kind));
				const lists = await Promise.all(ks.map(async (k) => ({ k, rows: await read({ kind: k.kind, from: MERGE }).catch(() => [] as Row[]) })));
				const people = new Map(lists.filter((l) => l.k.kind === K.person).flatMap((l) => l.rows.map((r) => [r.id, r.name as string])));
				// a record's own colour (a status), a task its state's
				const colors = new Map(lists.flatMap((l) => l.rows.filter((r) => typeof r.color === "string").map((r) => [r.id, r.color as string])));
				const nodes = lists.flatMap(({ k, rows }) =>
					rows.map((n) => ({
						id: n.id,
						kind: shortKind(k.kind),
						// a record without a label of its own (a Member) is named by its person
						name: !k.label && n.person && people.get(n.person as string) ? `${people.get(n.person as string)} (${shortKind(k.kind).toLowerCase()})` : String((k.label && n[k.label]) || n.id),
						status: typeof n.status === "string" ? n.status : null,
						color: colors.get(n.id) ?? colors.get(n.state as string) ?? null,
						instance: instanceOf(n.id),
					})),
				);
				const ids = new Set(nodes.map((n) => n.id));
				// an edge's kind is its relation's, as the package declares it (KindInfo; older instances: associations)
				const kindOfEdge = (k: KindInfo, f: string): EdgeKind =>
					k.hierarchy?.includes(f) ? "hierarchy" : k.dependency?.includes(f) ? "dependency" : "association";
				const edges = lists.flatMap(({ k, rows }) =>
					rows.flatMap((n) => [
						...k.single.filter((f) => typeof n[f] === "string").map((f) => ({ from: n.id, to: n[f] as string, type: f, edgeKind: kindOfEdge(k, f) })),
						...k.many.flatMap((f) => ((n[f] as string[]) ?? []).map((to) => ({ from: n.id, to, type: f, edgeKind: kindOfEdge(k, f) }))),
						// a space-scoped record is in its space (truss scope): the space → its records
						...(typeof n.space === "string" && k.kind !== K.space ? [{ from: n.space, to: n.id, type: "space", edgeKind: "hierarchy" as const }] : []),
					]),
				);
				return { nodes, edges: edges.filter((e) => ids.has(e.from) && ids.has(e.to)) };
			},
		},
		// chat's rules are the core's (chat/core): unread against my read mark, a record's thread, the first message
		// making it in the record's space, reaction toggles, read marks
		chat: {
			spaces: () => see<Space[]>("chat.spaces"),
			markdown,
			overview: (cb) => watched<ChatOverview>("chat.overview", null, cb),
			inbox: (space, cb) => watched<Thread[]>("chat.inbox", { space }, cb),
			async threads(q) {
				if (q.about && !q.space) {
					// a record's thread: the core's thread_for, as a list of at most one
					const id = await see<string | null>("chat.thread_for", { about: q.about });
					if (!id) return [];
					return (await see<Thread[]>("chat.threads", { about: q.about })).filter((t) => t.id === id);
				}
				return see<Thread[]>("chat.threads", { space: q.space ?? null, about: q.about ?? null });
			},
			messages: (thread) => see<Message[]>("chat.messages", { thread }),
			conversation: (to, cb) => watched<Conversation>("chat.conversation", to, (c, error) => cb(c ?? { thread: null, messages: [] }, error)),
			// optimistic in the core: the conversation shows it at once (pending); resolves once it landed
			async send(to, body) {
				const r = await act<{ id: string; thread: string }>("chat.send", { ...to, body });
				const m = await me();
				return { id: r.id, threadId: r.thread, by: { id: m.id, name: m.name }, body, at: null, reactions: [], pending: true };
			},
			react: async (message, emoji) => void (await act("chat.react", { message, emoji })),
			read: async (thread) => void (await act("chat.read", { thread })),
			async attach(message, files) {
				const [m] = await read({ kind: K.message, where: { id: message }, limit: 1 });
				const space = refOf(m?.space);
				if (!space) throw toApiError({ error: { type: "not_found" }, message: "the message is not here yet" });
				for (const blob of files) {
					const put = await net.blobs.put(space, blob);
					const file = await create(K.file, { space, name: blob.name });
					await create(K.version, { space, file, n: 1, sha256: put.sha256, blob: put.key, size: put.size, contentType: typeOf(blob), attachedTo: { add: [message] } });
				}
			},
			async attachments(thread) {
				const [t] = await read({ kind: K.thread, where: { id: thread }, limit: 1 });
				const space = refOf(t?.space);
				if (!space) return {};
				const [ms, vs] = await Promise.all([read({ kind: K.message, where: { thread }, limit: 5000 }), read({ kind: K.version, where: { space }, limit: 5000 })]);
				const ids = new Set(ms.map((m) => m.id as string));
				const sent = vs.filter((v) => ([] as unknown[]).concat(v.attachedTo ?? []).some((r) => ids.has(refOf(r) ?? "")));
				if (!sent.length) return {};
				const fs = await read({ kind: K.file, where: { id: { in: [...new Set(sent.map((v) => refOf(v.file) ?? ""))] } } });
				const out: Record<string, Attachment[]> = {};
				for (const v of sent)
					for (const r of ([] as unknown[]).concat(v.attachedTo ?? []))
						if (ids.has(refOf(r) ?? "")) (out[refOf(r)!] ??= []).push({ ...versionOf(v), name: String(fs.find((f) => f.id === refOf(v.file))?.name ?? "file") });
				return out;
			},
		},
		ontologies: {
			async list(instance) {
				const [rows, offered] = await Promise.all([read({ kind: K.ontology, from: instance ? [instance] : undefined }), instance ? ([] as Raw[]) : net.offered()]);
				const installed = rows.map((o) => {
					const pkg = typeof o.package === "string" ? JSON.parse(o.package) : ((o.package as Raw) ?? {});
					const latest = offered.find((p) => p.name === o.name);
					return toPackage(pkg, { installed: o.version as string, installedAt: (o.createdAt as string) ?? null, version: latest?.version ?? (o.version as string) });
				});
				const have = new Set(installed.map((p) => p.name));
				return [...installed, ...offered.filter((p) => !have.has(p.name)).map((p) => toPackage(p, { installed: null, installedAt: null, version: p.version }))];
			},
			async preview(name, version, given) {
				const pkg = (given as Raw | undefined) ?? (await registered(name));
				const plan = await ask<Raw>({ type: "install", package: pkg, dry_run: true });
				const what = (op: string) => (op.includes("Kind") ? "kind" : op.includes("Relation") ? "relation" : op.includes("Hook") ? "hook" : op.includes("Rule") ? "rule" : "field");
				return {
					name: plan.name,
					from: plan.from ?? null,
					to: plan.to ?? version,
					added: (plan.changes as Raw[]).map((c) => ({ what: what(c.op) as "kind", name: `${plan.name}.${String(c.path).replace(/^(kinds|extends)\./, "")}`, detail: c.detail ?? c.op })),
					refused: (plan.refused as Raw[]).map((c) => ({ name: c.path, reason: c.detail ?? c.op })),
					hooks: ((pkg.hooks as Raw[]) ?? []).map((h) => h.name ?? JSON.stringify(h)),
				};
			},
			async install(name, _version, _consent, given) {
				const pkg = (given as Raw | undefined) ?? (await registered(name));
				await ask({ type: "install", package: pkg });
			},
		},
		access: net.access,
		connections: net.connections,
		members: {
			invite: (space, roles) => net.invite(space, roles),
			async list() {
				const [rows, people, roles, spaces] = await Promise.all([
					read({ kind: K.member }),
					read({ kind: K.person }),
					read({ kind: K.role }),
					read({ kind: K.space }),
				]);
				const roleName = new Map(roles.map((r) => [r.id, r.name as string]));
				const personOf = new Map(people.map((p) => [p.id, p]));
				const spaceName = new Map(spaces.map((s) => [s.id, (s.name as string) ?? s.id]));
				const roleNames = (ids: unknown) => ((ids as string[]) ?? []).map((id) => roleName.get(id) ?? id);
				return [
					// global roles = instance-wide members
					...people
						.filter((p) => roleNames(p.roles).length)
						.map((p) => ({ id: `global:${p.id}`, person: { id: p.id, name: (p.name as string) ?? p.id, kind: "human" as const }, space: null, roles: roleNames(p.roles), status: "active" as const })),
					...rows.map((m) => {
						const p = personOf.get(m.person as string);
						return {
							id: m.id,
							person: { id: (m.person as string) ?? "", name: (p?.name as string) ?? "?", kind: "human" as const },
							space: m.space ? { id: m.space as string, name: spaceName.get(m.space as string) ?? (m.space as string) } : null,
							roles: roleNames(m.roles),
							status: m.status === "removed" ? ("removed" as const) : ("active" as const),
						};
					}),
				];
			},
			async roles() {
				return (await read({ kind: K.role })).map((r) => ({ id: r.id, name: r.name as string, permissions: (r.permissions as string[]) ?? [] }));
			},
			async add(person, roles, space) {
				const all = await read({ kind: K.role });
				const roleIds = all.filter((r) => roles.includes(r.name as string)).map((r) => r.id);
				let pid = person.id;
				if (!pid && person.name) {
					const found = (await read({ kind: K.person })).find((p) => String(p.name).toLowerCase() === person.name!.toLowerCase());
					pid = found?.id ?? (await create(K.person, { name: person.name, type: "human" }));
				}
				if (!pid) throw new ApiError("who?", 400);
				if (space) await create(K.member, { space, person: pid, roles: { add: roleIds } });
				else await update(K.person, pid, { roles: { add: roleIds } });
			},
			async remove(id) {
				if (id.startsWith("global:")) throw new ApiError("instance roles are removed by an admin in the CLI", 400);
				await update(K.member, id, { status: "removed" });
			},
		},
		tokens: net.tokens,
		auth: net.auth,
		cloudflare: net.cloudflare,
		// the Deployments this home holds (the platform's Fleet space), each with its Instance (ownedBy) and Hosting (of);
		// a home without hq.infra (or without any) manages none
		knowledge: {
			async batch(id) {
				const ps = await read({ kind: K.proposal, where: { batch: id }, limit: 5000 });
				const ids = [...new Set(ps.flatMap((p) => refs(p.sources)))];
				const srcs = ids.length ? await read({ kind: K.source, where: { id: { in: ids } }, limit: ids.length }) : [];
				const byId = new Map<string, Raw>(srcs.map((s) => [s.id as string, s as Raw]));
				return ps
					.map((p) => ({
						id: p.id as string,
						key: (p.key as string) ?? "",
						status: (p.status as string) ?? "draft",
						kind: String(p.targetKind ?? "").split(".").pop()!,
						op: (p.op as string) ?? "create",
						summary: (p.summary as string) ?? "",
						why: (p.why as string) ?? null,
						error: (p.error as string) ?? null,
						order: (p.order as number) ?? 0,
						fields: patchRows(p.patch),
						questions: ((p.questions as Raw[] | undefined) ?? []).map((x) => ({ field: str(x.field), waitsFor: str(x.waitsFor), text: str(x.text) ?? "" })),
						answers: (p.answers as Record<string, string> | undefined) ?? {},
						sources: refs(p.sources)
							.map((s) => byId.get(s))
							.flatMap((s) => (s ? [toSource(s)] : [])),
					}))
					.sort((a, b) => a.order - b.order || a.key.localeCompare(b.key))
					.map(({ order: _o, ...p }) => p);
			},
			async decide(ids, status) {
				for (const id of ids) await update(K.proposal, id, { status });
				if (status === "approved" && ids[0]) await wakeHousekeeper(ids[0], `Approved ${ids.length} proposal${ids.length > 1 ? "s" : ""}: please apply.`);
			},
			async answer(id, answers) {
				await update(K.proposal, id, { answers });
				await wakeHousekeeper(id, `Answered: ${Object.entries(answers).map(([k, v]) => `${k} = ${v}`).join(", ")}. Please apply.`);
			},
			async sources(at) {
				if (!(await has(K.source))) return [];
				const found = await Promise.all(["file", "thread", "message", "id"].map((f) => read({ kind: K.source, where: { [f]: at }, limit: 5000 })));
				const ss = [...new Map(found.flat().map((s) => [s.id as string, s as Raw])).values()];
				const ids = [...new Set(ss.flatMap((s) => refs(s.supports)))];
				// what each record is, from the proposal that wrote it (the last one not rejected)
				const ps = ids.length ? await read({ kind: K.proposal, where: { target: { in: ids } }, limit: 5000 }) : [];
				const by = new Map<string, Raw>();
				for (const p of ps) if (p.status !== "rejected" || !by.has(p.target as string)) by.set(p.target as string, p);
				return ss.map((s) => ({
					...toSource(s),
					records: refs(s.supports).map((id) => {
						const p = by.get(id);
						const kind = String(p?.targetKind ?? local(id).split("_")[0]).split(".").pop()!;
						return { id, kind, label: String(p?.summary ?? id).replace(`${kind} `, ""), status: str(p?.status) };
					}),
				}));
			},
		},
		fleet: {
			async list() {
				const [ds, is, hs] = await Promise.all([read({ kind: K.deployment }), read({ kind: K.instance }), read({ kind: K.hosting })]).catch(() => [[], [], []] as Raw[][]);
				const first = (v: unknown) => ([] as unknown[]).concat(v ?? [])[0] as string | undefined;
				const hostOf = (u: unknown) => (typeof u === "string" && URL.canParse(u) ? new URL(u).host : null);
				return ds
					.filter((d) => d.status !== "retired")
					.map((d) => {
						const o = (d.observed as Raw | undefined) ?? {};
						const host = hostOf(o.url) ?? hostOf(d.url);
						// the instance it runs: its ownedBy Instance (any ref), else the one on its address
						const i = is.find((x) => refs(d.ownedBy).includes(x.id)) ?? (host ? is.find((x) => x.domain === host || hostOf(x.url) === host) : undefined);
						const h = hs.find((x) => x.id === first(d.of));
						// an operator long silent: its observation (available among it) may be stale, no Update from it
						const silent = operatorSilentSince(o);
						const meters = ((o.usage as Raw[] | undefined) ?? []).map((m) => ({ ...m, scope: m.scope ?? null, limit: m.limit ?? null, resetsAt: null, source: "", error: null }));
						return {
							id: d.id as string,
							// always a short name (Michael): never the id or the summary; unresolved: its address' first label
							instance: (i?.name as string) ?? host?.split(".")[0]?.replace(/^hq-/, "") ?? "\u2026",
							instanceId: (i?.id as string) ?? null,
							url: (o.url as string) ?? (d.url as string) ?? null,
							domain: (i?.domain as string) ?? null,
							region: (i?.region as string) ?? null,
							status: (d.status as string) ?? "",
							build: (d.build as string) ?? null,
							running: (o.build as string) ?? null,
							ready: o.ready === true,
							error: (o.error as string) ?? null,
							hosting: (h?.id as string) ?? null,
							account: (h?.account as string) ?? null,
							usage: Meter.array().catch([]).parse(meters),
							available: silent ? null : ((o.available as string) ?? null),
							auto: d.auto === true,
							held: silent ? `operator silent since ${silent.slice(0, 16).replace("T", " ")} UTC` : typeof o.held === "string" && o.held ? o.held : null,
						};
					})
					.sort((a, b) => a.instance.localeCompare(b.instance));
			},
			async update(id, patch) {
				await update(K.deployment, id, patch);
			},
		},
		secrets: {
			async list(project) {
				// a connected instance's project: its secrets' metadata through the home's grant; the sealed store
				// (recipients, writes) answers on that instance only
				const remote = instanceOf(project);
				const [rows, people, devices, keys] = await Promise.all([
					read({ kind: K.secret, where: { space: project, status: "active" }, order: [{ field: "profile" }, { field: "key" }], from: remote ? [remote] : undefined }),
					read({ kind: K.person }),
					// mine only (truss reads a person its own); none before truss 1.4.0
					read({ kind: K.deviceKey, where: { status: "active" } }).catch(() => [] as Raw[]),
					remote ? Promise.resolve([] as string[]) : net.sealed.recipients(project),
				]);
				// reads are the record's `read` events: their count, the last among the newest 500
				const reads = rows.length
					? await ask<Record<string, { count: number; events: Raw[] }>>({ type: "history", ids: rows.map((r) => r.id), field: "read", limit: 500 }).catch(() => ({}) as Record<string, never>)
					: {};
				return {
					secrets: rows.map((s) => {
						const h = reads[s.id];
						const lastRead = h?.events.at(-1);
						return {
							id: s.id,
							profile: s.profile as string,
							key: s.key as string,
							status: s.status === "removed" ? ("removed" as const) : ("active" as const),
							rev: (s.rev as number) ?? null,
							changedAt: (s.updatedAt as string) ?? null,
							recipients: (s.recipients as string[]) ?? [],
							reads: h ? h.count : null,
							lastReadAt: (lastRead?.at as string) ?? null,
							lastReadBy: lastRead?.by ? { name: lastRead.by.name as string } : null,
						};
					}),
					recipients: keys.map((key) => {
						const d = devices.find((x) => x.ageKey === key);
						const owner = (id: unknown) => people.find((p) => p.id === id)?.name as string | undefined;
						const names = d ? [`${owner(d.person) ?? "?"} \u00b7 ${d.name}`] : people.filter((p) => p.ageKey === key).map((p) => p.name as string);
						return { key, names, ...(d ? { device: d.id as string } : {}) };
					}),
					...(remote ? { remote: true } : {}),
				};
			},
			save: (project, profile, key, value) => net.sealed.save(project, profile, key, value),
			remove: (project, profile, key) => net.sealed.remove(project, profile, key),
			read: (project, refs) => net.sealed.read(project, refs),
			reseal: (project, plan) => net.sealed.reseal(project, plan),
			addDevice: async (name, ageKey) => create(K.deviceKey, { name, ageKey, person: (await me()).id }),
			revokeDevice: async (id) => void (await update(K.deviceKey, id, { status: "revoked" })),
		},
		files: {
			async list(space, folder) {
				const [fs, xs, vs] = await Promise.all([read({ kind: K.folder, where: { space, status: "active" } }), read({ kind: K.file, where: { space, status: "active" } }), read({ kind: K.version, where: { space } })]);
				const latest = new Map<string, FileVersion>();
				for (const v of vs.map(versionOf)) if (!(latest.get(v.file)?.n! >= v.n)) latest.set(v.file, v);
				const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
				return {
					folders: fs.map((f) => ({ id: f.id as string, name: String(f.name), space, parent: refOf(f.parent) })).filter((f) => f.parent === folder).sort(byName),
					files: xs.map((f) => ({ id: f.id as string, name: String(f.name), space, folder: refOf(f.folder), latest: latest.get(f.id) ?? null })).filter((f) => f.folder === folder).sort(byName),
				};
			},
			async versions(file) {
				return (await read({ kind: K.version, where: { file } })).map(versionOf).sort((a, b) => b.n - a.n);
			},
			async upload(to, blob) {
				const f = "file" in to ? (await read({ kind: K.file, where: { id: to.file } }))[0] : null;
				const space = f ? (refOf(f.space) ?? "") : (to as { space: string }).space;
				const put = await net.blobs.put(space, blob);
				const file = f ? (f.id as string) : await create(K.file, { space, name: blob.name, ...("folder" in to && to.folder ? { folder: to.folder } : {}) });
				const have = f ? (await read({ kind: K.version, where: { file } })).map(versionOf).sort((a, b) => b.n - a.n) : [];
				// the same bytes as the newest version: no new version
				if (have[0]?.sha256 === put.sha256) return file;
				await create(K.version, { space, file, n: (have[0]?.n ?? 0) + 1, sha256: put.sha256, blob: put.key, size: put.size, contentType: typeOf(blob) });
				return file;
			},
			folder: (space, name, parent) => create(K.folder, { space, name, ...(parent ? { parent } : {}) }),
			async open(v) {
				const bytes = await net.blobs.get(v.blob);
				const typed = new Blob([bytes], { type: v.contentType });
				const text = textual(v.contentType) ? await typed.text() : null;
				const doc = text !== null && /markdown/.test(v.contentType) ? await markdown(text) : null;
				return { url: URL.createObjectURL(typed), text, doc };
			},
		},
		blobs: net.blobs,
	};

	async function registered(name: string): Promise<Raw> {
		const p = (await net.offered()).find((x) => x.name === name);
		if (!p) throw new ApiError(`${name}: not in the registry`, 404);
		return p;
	}
}

/** A package JSON (truss:Interface/ontology) → the page's shape; kinds named `<pkg>.<Kind>`, counts unknown. */
function toPackage(pkg: Raw, v: { installed: string | null; installedAt: string | null; version: string }): Package {
	const field = (f: Raw) => ({ name: f.name, type: f.type === "enum" ? `enum(${(f.values ?? []).join(", ")})` : `${f.type}${f.list ? "[]" : ""}`, required: !!f.required, many: !!f.list });
	const relation = (r: Raw) => ({ name: r.name, to: r.to, many: !!r.many, inverse: r.inverse ?? null });
	return {
		name: pkg.name as string,
		version: v.version,
		description: (pkg.description ?? pkg.title ?? null) as string | null,
		installed: v.installed,
		installedAt: v.installedAt,
		base: ["truss", "hq.tasks", "hq.chat"].includes(pkg.name),
		hooks: ((pkg.hooks ?? []) as Raw[]).map((h) => (typeof h === "string" ? h : (h.name ?? JSON.stringify(h)))),
		kinds: [
			...((pkg.kinds ?? []) as Raw[]).map((k) => ({
				name: `${pkg.name}.${k.name}`,
				description: k.intent ?? k.description ?? null,
				fields: ((k.fields ?? []) as Raw[]).map(field),
				relations: ((k.relations ?? []) as Raw[]).map(relation),
				count: null,
			})),
			...((pkg.extends ?? []) as Raw[]).map((x) => ({
				name: `${x.kind} (extended)`,
				description: null,
				fields: ((x.fields ?? []) as Raw[]).map(field),
				relations: ((x.relations ?? []) as Raw[]).map(relation),
				count: null,
			})),
		],
	};
}

/** The core's `detail` (a view over the replica) → the side panel. */
/** A record's prose: its kind's markdown field (shown as Markdown), else `body` or `description` (plain text). */
function prose(f: Raw, info: KindInfo | null) {
	const md = info?.fields?.find((x) => x.type === "markdown" && f[x.name] != null)?.name;
	const key = md ?? (f.body != null ? "body" : f.description != null ? "description" : null);
	return { key, body: key ? String(f[key]) : null, markdown: !!md };
}

function fromReplica(d: Raw, info: KindInfo | null) {
	const f = (d.fields ?? {}) as Raw;
	const label = info?.label ?? null;
	const edges = new Set([...(info?.single ?? []), ...(info?.many ?? [])]);
	const p = prose(f, info);
	const skip = new Set([label, p.key, "status", "body", "description", "rank", "links", "space", "address", "createdAt", "updatedAt", "createdBy", "updatedBy"]);
	const scalars = Object.entries(f).flatMap(([k, v]) => (skip.has(k) || edges.has(k) ? [] : text(k, v)));
	const end = (x: Raw) => ({ key: x.field as string, value: String(x.label ?? x.id), ref: x.id as string, kind: x.kind ?? undefined });
	const out = ((d.out as Raw[]) ?? []).filter((x) => x.field !== "context" && x.field !== "space");
	return {
		id: d.id,
		kind: shortKind(d.kind),
		fullKind: d.kind ?? null,
		instance: instanceOf(d.id),
		name: String(d.label ?? d.id),
		status: typeof f.status === "string" ? f.status : null,
		body: p.body,
		markdown: p.markdown,
		fields: [
			...(d.space && d.space.id !== d.id ? [{ key: "space", value: String(d.space.label ?? d.space.id), ref: d.space.id }] : []),
			...out.map(end),
			...scalars,
			...text("created", f.createdAt),
			...text("by", d.createdBy?.name),
			...text("updated", f.updatedAt),
		],
		backlinks: ((d.in as Raw[]) ?? []).filter((x) => x.field !== "space").map(end),
		links: ((f.links as Raw[]) ?? []).map((l) => ({ url: l.url, kind: l.kind ?? "url", title: l.title ?? null, addedAt: l.addedAt ?? f.updatedAt ?? "" })),
		events: ((d.history as Raw[]) ?? []).map((h) => ({ at: h.at ?? "", by: h.by?.name ?? "?", field: h.field, from: h.from ?? null, to: h.to ?? null, note: h.note ?? null })),
		context: ((d.out as Raw[]) ?? [])
			.filter((x) => x.field === "context")
			.map((x) => ({ id: x.id, kind: shortKind(String(x.kind ?? "Entity")), label: String(x.label ?? x.id), note: x.note ?? null })),
	};
}

const HEADER = new Set(["__typename", "id", "address", "kind", "createdAt", "updatedAt", "createdBy", "space", "links", "history", "out", "in"]);

/** The core's record answer → the side panel: scalars, outgoing edges (refs), incoming (backlinks), context, history. */
function toDetail(e: Raw, info: KindInfo | null) {
	// a remote kind this instance does not know: kit's label rule (LABELS) on the record itself
	const label = info?.label ?? LABELS.find((l) => typeof e[l] === "string") ?? null;
	const own = Object.fromEntries(Object.entries(e).filter(([k, v]) => !HEADER.has(k) && (v === null || typeof v !== "object" || Array.isArray(v))));
	const p = prose(own, info);
	const skip = new Set([label, p.key, "status", "body", "description", "rank"]);
	const scalars = Object.entries(own).flatMap(([k, v]) => (skip.has(k) ? [] : text(k, v)));
	const end = (x: Raw) => ({ value: (x.node?.label as string) ?? x.address, ref: (x.node?.id as string) ?? x.address, kind: x.node?.kind ?? undefined });
	const out = ((e.out as Raw[]) ?? []).filter((x) => x.field !== "context");
	const context: ContextItem[] = ((e.out as Raw[]) ?? [])
		.filter((x) => x.field === "context")
		.map((x) => ({ id: end(x).ref, kind: shortKind(String(x.node?.kind ?? "Entity")), label: end(x).value, note: x.note ?? null }));
	return {
		id: e.id,
		kind: shortKind((e.kind as string) ?? e.__typename),
		fullKind: e.kind ?? null,
		instance: instanceOf(e.id),
		name: String((label && own[label]) ?? e.id),
		status: typeof own.status === "string" ? own.status : null,
		body: p.body,
		markdown: p.markdown,
		fields: [
			...(e.space && e.space.id !== e.id ? [{ key: "space", value: (e.space.label as string) ?? e.space.id, ref: e.space.id }] : []),
			...out.filter((x) => x.field !== "space").map((x) => ({ key: x.field, ...end(x) })),
			...scalars,
			...text("created", e.createdAt),
			...text("by", e.createdBy?.name),
			...text("updated", e.updatedAt),
		],
		backlinks: ((e.in as Raw[]) ?? []).filter((x) => x.field !== "space").map((x) => ({ key: x.field, ...end(x) })),
		links: e.links ?? [],
		events: ((e.history?.nodes as Raw[]) ?? []).map((h) => ({ at: h.at, by: h.by?.name ?? "?", field: h.field, from: h.from ?? null, to: h.to ?? null, note: h.note ?? null })),
		context,
	};
}
